import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { toLonLat } from 'ol/proj';
import { useConfirm } from 'material-ui-confirm';
import { DndContext, closestCenter } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, arrayMove } from '@dnd-kit/sortable';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { platoonGroupsResourceType } from '../../../entities/vehicles/trackPointsResource/redux/trackPointsResource.slice';
import convertFormatDate from '../../../shared/utils/convertFormatDate';
import config from '../../../shared/api/config';
import { followModeActions, FollowModeEnum } from '../../../entities/map/followMode/redux/followMode.slice';
import { useAppDispatch, useAppSelector } from '../../../app/store';
import useTimeZone from '../../../entities/timeZone/hooks/useTimeZone';
import VehicleHistoryToolbarWidget from '../../../widgets/vehicles/VehicleHistoryToolbarWidget/VehicleHistoryToolbarWidget';
import Container from './styles/Container';
import { useLazyFindGeoZoneByCoordsQuery } from '../../../entities/geoZones/geoZone/redux/geoZone.api';
import { parsePolygonWHoles } from '../../../shared/geo-map/lib';
import mapNames from '../../../entities/map/constants';
import reducerPath from '../../../app/reducerPath';
import VehicleMapWidget from '../../../widgets/vehicles/VehicleMapWidget/VehicleMapWidget';
import isValidDate from '../../../shared/utils/isValidDate';
import Panel from './styles/Panel';
import MapBlock from './styles/MapBlock';
import RightSide from './styles/RightSide';
import {
  useVehicleChartSensorsState,
  useVehicleHistoryDates,
  useVehicleHistoryExtent,
  useVehicleHistoryGeoZones,
  useVehicleHistoryPlayback,
} from '../../../entities/vehicles/hooks';
import {
  getDisplayCoordinates,
  getExtentCoordsAroundPoint,
  getFormattedLastDate,
} from '../../../entities/vehicles/utils';
import SensorChart from '../../../widgets/vehicles/SensorChart';
import VehicleSensorCharts from '../../../widgets/vehicles/VehicleSensorCharts';
import VehicleHistorySettings from '../../../widgets/vehicles/VehicleHistorySettings/VehicleHistorySettings';
import SortablePanelItem from './SortablePanelItem';
import LeftSide from './styles/LeftSide';

// Начальный масштаб
export const zoom = 15;

// Отображает вкладку "Карта" с картой и маршрутом, пройденным ТС
function VehicleMapPanel({ vehicle, data, getTrackPointsResourceParams, vehicleId }) {
  const [markers, setMarkers] = useState([]);
  const [enableGeozoneNames, setEnableGeozoneNames] = useState(true);
  const [enableMapRoutes, setEnableMapRoutes] = useState(true);
  const [speedLimit, setSpeedLimit] = useState(null);
  const [stopTime, setStopTime] = useState(null);
  const [disableExternalExtent, setDisableExternalExtent] = useState(false);

  const [sensorsForChart, setSensorsForChart] = useVehicleChartSensorsState([]);
  const [panelOrder, setPanelOrder] = useState(['map']);
  const [sensorData, setSensorData] = useState({ sensorDataMap: null, thresholdsMap: null, domain: undefined });

  // синхронизируем panelOrder с sensorsForChart
  useEffect(() => {
    setPanelOrder((prev) => {
      const sensorIds = new Set(sensorsForChart.map((s) => String(s.sensorTypeId)));
      const filtered = prev.filter((id) => id === 'map' || sensorIds.has(id));
      const existingIds = new Set(filtered);
      const newSensors = sensorsForChart
        .filter((s) => !existingIds.has(String(s.sensorTypeId)))
        .map((s) => String(s.sensorTypeId));
      return [...filtered, ...newSensors];
    });
  }, [sensorsForChart]);

  const mapName = mapNames.vehicleStory;
  const currentZoom = useAppSelector((state) => state[`${reducerPath.positionMap}`][mapName])?.zoom;
  const isNeedHideGozoneNames = currentZoom && currentZoom < 12;

  const dispatch = useAppDispatch();
  useEffect(() => {
    dispatch(followModeActions.changeMode({ [FollowModeEnum.isActive]: true }));
    setDisableExternalExtent(false);
  }, [dispatch, vehicleId]);

  const confirm = useConfirm();
  const [findGeoZoneByCoords] = useLazyFindGeoZoneByCoordsQuery();

  const timeZone = useTimeZone();

  // Использование вспомогательных hooks
  const {
    coordsState,
    setCoordsState,
    runRec,
    speedRec,
    setSpeedRec,
    setPositionHistory,
    handleStart: playbackHandleStart,
    handleStop: playbackHandleStop,
  } = useVehicleHistoryPlayback(data?.dataTrackPointsResource?.data, vehicleId);

  const { dateStart, setDateStart, dateFinish, setDateFinish, intervalOption, setIntervalOption, getCustomHistory } =
    useVehicleHistoryDates(timeZone, getTrackPointsResourceParams, vehicleId);

  const coords = data?.dataTrackPointsResource?.data;
  const { extent, changeExtent } = useVehicleHistoryExtent(coords, disableExternalExtent);
  const { geoZones, setGeoZones, isSearchGeozoneActive, setIsSearchGeozoneActive } = useVehicleHistoryGeoZones(coords);

  // Запускает воспроизведение маршрута
  const handleStart = () => {
    if (!disableExternalExtent) {
      setCoordsState([coords[0]]);
    }
    setDisableExternalExtent(true);
    playbackHandleStart();
  };

  // Останавливает воспроизведение маршрута
  const handleStop = () => {
    playbackHandleStop();
  };

  // Экспортирует файл с точками маршрута
  const getFile = useCallback(() => {
    // TODO: переписать token на стор
    // eslint-disable-next-line no-undef
    const token = window.localStorage.getItem('token') || window.sessionStorage.getItem('token');

    const params = {
      access_token: token,
      vehicleId: String(vehicleId),
      startTime: String(new Date(dateStart).toISOString()),
      finishTime: String(new Date(dateFinish).toISOString()),
    };

    const paramsString = new URLSearchParams(params).toString();

    if (token && String(vehicleId)) {
      // eslint-disable-next-line no-undef
      window.open(`${config.vstorageUrl}/track-points/export?${paramsString}`, '_blank');
    } else {
      confirm({
        title: 'Ошибка',
        confirmationText: 'Отмена',
        cancellationText: null,
        description: `Произошла ошибка при загрузке файла, попробуйте позже.`,
      });
    }
  }, [vehicleId, dateFinish, dateStart, confirm]);

  const coordinates = useMemo(() => getDisplayCoordinates(runRec, coordsState, coords), [runRec, coordsState, coords]);

  const dateForChart = useMemo(
    () => (coordsState?.length ? coordsState[coordsState.length - 1]?.date : coords?.[0]?.date || ''),
    [coordsState, coords],
  );

  const formattedLastDate = getFormattedLastDate(data?.dataTrackPointsResource?.data, coordsState, timeZone);

  // Перемещает карту к текущей позиции ТС
  const handleCurrentPosition = () => {
    handleStop();
    setDisableExternalExtent(false);
    changeExtent(getExtentCoordsAroundPoint(coordsState.length ? coordsState : coords));
  };

  // Обрабатывает окончание перетаскивания панелей сенсоров
  const handlePanelDragEnd = (event) => {
    const { active, over } = event;

    if (!over || active.id === over.id) return;

    setPanelOrder((prev) => {
      const oldIndex = prev.findIndex((id) => id === active.id);
      const newIndex = prev.findIndex((id) => id === over.id);
      const newOrder = arrayMove(prev, oldIndex, newIndex);

      // если перетащили сенсор — обновляем порядок в sensorsForChart
      const oldSensorIds = prev.filter((id) => id !== 'map');
      const newSensorIds = newOrder.filter((id) => id !== 'map');
      const changed = oldSensorIds.some((id, i) => id !== newSensorIds[i]);

      if (changed) {
        setSensorsForChart((prevSensors) => {
          const sensorMap = new Map(prevSensors.map((s) => [String(s.sensorTypeId), s]));
          return newSensorIds.map((id) => sensorMap.get(id)).filter(Boolean);
        });
      }

      return newOrder;
    });
  };

  // Удаляет график сенсора из панели
  const handleDeleteChart = (sensorTypeId) => {
    setSensorsForChart((prev) => prev.filter((s) => String(s.sensorTypeId) !== String(sensorTypeId)));
  };

  // Обновляет данные сенсоров для графиков
  const handleDataUpdate = useCallback((sensorDataPayload) => {
    setSensorData(sensorDataPayload);
  }, []);

  // Экспортирует треки ТС
  const handleExportTracks = () => {
    if (isValidDate(dateStart) && isValidDate(dateFinish)) {
      getFile();
    }
  };

  // Обрабатывает клик по карте для поиска геозон
  const handleClickMap = useCallback(
    async (_, clickedFeature, eventCoords) => {
      if (!isSearchGeozoneActive) return;

      const [lon, lat] = toLonLat(eventCoords);
      const findedGeozonesData = await findGeoZoneByCoords({ lat, lon }).unwrap();

      setGeoZones((prev) => {
        if (clickedFeature) {
          const zoneId = clickedFeature.get('zoneId');
          if (prev.some((geo) => geo.geozoneId === zoneId)) {
            return prev.filter((geo) => geo.geozoneId !== zoneId);
          }
        }

        const newGeozones = findedGeozonesData?.data?.filter(
          (found) => !prev.some((existing) => existing.geozoneId === found.geozoneId),
        );

        if (newGeozones.length) {
          return [...prev, { ...newGeozones[0], polygon: parsePolygonWHoles(newGeozones[0].area) }];
        }

        return prev;
      });
    },
    [isSearchGeozoneActive, findGeoZoneByCoords],
  );

  // Сохраняет маркер текущей позиции
  const handleSaveMarker = () => {
    setMarkers((prev) => [
      ...prev,
      coordsState.map((point) => ({
        longitude: point.longitude,
        latitude: point.latitude,
      })).length - 1,
    ]);
  };

  return (
    <Container>
      <VehicleHistoryToolbarWidget
        vehicleId={vehicleId}
        isSearchGeozoneActive={isSearchGeozoneActive}
        onClickSearchGeozone={() => setIsSearchGeozoneActive((prev) => !prev)}
        onClickExportData={handleExportTracks}
        dateFrom={dateStart}
        dateTo={dateFinish}
      />
      <Panel>
        <LeftSide>
          <VehicleSensorCharts
            vehicleId={vehicleId}
            sensors={sensorsForChart}
            dateStart={dateStart}
            dateFinish={dateFinish}
            onDataUpdate={handleDataUpdate}
          />
          <DndContext collisionDetection={closestCenter} onDragEnd={handlePanelDragEnd} modifiers={[restrictToVerticalAxis]}>
            <SortableContext items={panelOrder} strategy={verticalListSortingStrategy}>
              {panelOrder.map((panelId) => {
                if (panelId === 'map') {
                  return (
                    <SortablePanelItem key="map" id="map">
                      {coords?.length ? (
                        <VehicleMapWidget
                          mapName={mapName}
                          zoom={zoom}
                          extent={extent}
                          isAnimateOnExtent={!disableExternalExtent}
                          coordinates={coordinates}
                          speedLimit={speedLimit}
                          stopTime={stopTime}
                          geoZones={geoZones}
                          enableGeozoneNames={enableGeozoneNames}
                          enableMapRoutes={enableMapRoutes}
                          isNeedHideGozoneNames={isNeedHideGozoneNames}
                          vehicle={vehicle}
                          coordsState={coordsState}
                          data={data}
                          timeZone={timeZone}
                          convertFormatDate={convertFormatDate}
                          markers={markers}
                          handleClickMap={handleClickMap}
                          coords={coords}
                        />
                      ) : (
                        <MapBlock>Нет данных для отображения.</MapBlock>
                      )}
                    </SortablePanelItem>
                  );
                }

                const sensor = sensorsForChart.find((s) => String(s.sensorTypeId) === panelId);
                if (!sensor) return null;
                const itemId = sensor.sensorTypeId;

                return (
                  <SortablePanelItem key={panelId} id={panelId}>
                    <SensorChart
                      sensorName={`${sensor.name}${sensor.units ? `, ${sensor.units}` : ''}` || `Сенсор ${panelId}`}
                      data={sensorData.sensorDataMap?.get(itemId)}
                      selectedDate={dateForChart}
                      onDelete={() => handleDeleteChart(itemId)}
                      thresholds={sensorData.thresholdsMap?.get(itemId)}
                      domain={sensorData.domain}
                    />
                  </SortablePanelItem>
                );
              })}
            </SortableContext>
          </DndContext>
        </LeftSide>
        <RightSide>
          <VehicleHistorySettings
            intervalOption={intervalOption}
            setIntervalOption={setIntervalOption}
            setDisableExternalExtent={setDisableExternalExtent}
            dateStart={dateStart}
            setDateStart={setDateStart}
            dateFinish={dateFinish}
            setDateFinish={setDateFinish}
            isValidDate={isValidDate}
            getCustomHistory={getCustomHistory}
            handleExportTracks={handleExportTracks}
            handleCurrentPosition={handleCurrentPosition}
            speedLimit={speedLimit}
            setSpeedLimit={setSpeedLimit}
            stopTime={stopTime}
            setStopTime={setStopTime}
            runRec={runRec}
            handleStart={handleStart}
            handleStop={handleStop}
            speedRec={speedRec}
            setSpeedRec={setSpeedRec}
            coordsState={coordsState}
            coords={coords}
            setPositionHistory={setPositionHistory}
            formattedLastDate={formattedLastDate}
            onSaveMarker={handleSaveMarker}
            sensors={sensorsForChart}
            onClickAdd={setSensorsForChart}
            vehicleId={vehicleId}
            enableGeozoneNames={enableGeozoneNames}
            setEnableGeozoneNames={setEnableGeozoneNames}
            enableMapRoutes={enableMapRoutes}
            setEnableMapRoutes={setEnableMapRoutes}
            timeZone={timeZone}
          />
        </RightSide>
      </Panel>
    </Container>
  );
}

VehicleMapPanel.propTypes = {
  vehicle: PropTypes.objectOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.object])),
  data: PropTypes.shape({
    dataTrackPointsResource: PropTypes.arrayOf(PropTypes.shape(platoonGroupsResourceType)),
  }),
  getTrackPointsResourceParams: PropTypes.func,
  vehicleId: PropTypes.number,
};

VehicleMapPanel.defaultProps = {
  vehicle: null,
  data: null,
  getTrackPointsResourceParams: null,
  vehicleId: PropTypes.number,
};

export default VehicleMapPanel;
